<template>
  <client-only>
    <swiper-container
      ref="swiperRef"
      :class="props.swiperContainerClass"
      v-bind="swiperOptions"
      :init="false"
      @swiperafterinit="handleSwiperInit"
      @swiperslidechange="handleSlideChange"
      @swipersnapindexchange="handleSnapIndexChange"
    >
      <swiper-slide v-for="(item, index) in props.data" :key="item" :class="props.swiperSlideClass">
        <slot :item="item" :index="index" />
      </swiper-slide>
    </swiper-container>
    <template #fallback>
      <slot name="skeleton" />
    </template>
  </client-only>
</template>

<script setup lang="ts" generic="T">
import type { Swiper } from 'swiper'
import type { SwiperOptions } from 'swiper/types'

interface Props {
  data: readonly T[]
  autoplay?: boolean
  swiperOptions?: SwiperOptions
  swiperContainerClass?: string | any[]
  swiperSlideClass?: string | any[]
}

const props = defineProps<Props>()

const emit = defineEmits(['slideInit', 'slideChange'])

const swiperRef = ref<HTMLElement>()
const swiper = ref<Swiper>()

const isBeginning = ref(true)
const isEnd = ref(false)

const swiperOptions = computed<SwiperOptions>(() => ({
  autoplay: props.data.length > 1 && props.autoplay && { delay: 3000 },
  ...props.swiperOptions,
  loop: props.data.length > 1 && !!props.swiperOptions?.loop,
}))

defineExpose({ swiperRef, swiper, isBeginning, isEnd })

watch(
  swiperOptions,
  () => {
    if (!swiperRef.value || !swiper.value) return

    // Object.assign(swiperRef.value, option)
    swiper.value.update()
  },
  { deep: true }
)

onMounted(() => {
  nextTick(() => {
    if (swiperRef.value) {
      const swiperContainerEl = swiperRef.value as HTMLElement & {
        initialize: () => void
      }

      Object.assign(swiperContainerEl, swiperOptions.value)

      swiperContainerEl.initialize()
    } else {
      throw createError({
        message: `Could not create carousel. Missing DOM element <swiper-container />.`,
        statusCode: 500,
        statusMessage: `Internal Server Error`,
      })
    }
  })
})

onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy()
  }
})

const renewAutoplay = () => {
  if (!swiper.value || !props.autoplay) {
    return
  }

  swiper.value.autoplay.stop()
  swiper.value.autoplay.start()
}

const handleSwiperInit = (e: CustomEvent<[Swiper]>) => {
  const swiperInstance = e.detail[0]
  swiper.value = swiperInstance
  emit('slideInit', e)
}

const handleSlideChange = (e: CustomEvent<[Swiper]>) => {
  const swiperInstance = e.detail[0]

  renewAutoplay()
  emit('slideChange', swiperInstance.activeIndex)
}

function handleSnapIndexChange(e: CustomEvent<[Swiper]>) {
  const swiperInstance = e.detail[0]

  setTimeout(() => {
    isBeginning.value = swiperInstance.isBeginning
    isEnd.value = swiperInstance.isEnd
  }, swiperOptions.value.speed || 300)
}
</script>
